import {
  Caption1,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowDownload16Regular,
  CloudArrowUp20Regular,
  Delete16Regular,
  Dismiss24Regular,
  Document16Regular,
  DocumentPdf16Regular,
  Edit16Regular,
  Link16Regular,
  Open16Regular,
  Star16Filled,
  Star16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton, FuiInput } from "@/components/fui-control";
import type { AttachmentLink } from "@/engine/types";
import { extractClipboardImageFiles } from "@/lib/clipboard-image";
import { formatDate } from "@/lib/date-format";
import { formatBytes } from "@/lib/format";
import { desktopDownloadClick } from "@/desktop/file-io";
import { useCatalogStore } from "@/store/catalog-store";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    height: "100%",
    minHeight: 0,
    overflowY: "auto",
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  groupHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
  },
  filterGroup: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    backgroundColor: tokens.colorNeutralBackground3,
    padding: "2px",
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
  },
  filterChip: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    backgroundColor: "transparent",
    border: "none",
    padding: "2px 8px",
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    cursor: "pointer",
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: tokens.colorStrokeFocus2 },
  },
  filterChipActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
  },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    ...shorthands.border("1.5px", "dashed", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: "pointer",
    transitionProperty: "border-color, background-color",
    transitionDuration: tokens.durationFast,
    ":hover": {
      ...shorthands.borderColor(tokens.colorCompoundBrandStroke),
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  dropzoneActive: {
    ...shorthands.borderColor(tokens.colorCompoundBrandStroke),
    backgroundColor: tokens.colorNeutralBackground2Hover,
  },
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  imageCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  imageThumbWrap: {
    display: "block",
    height: "80px",
    width: "100%",
    padding: 0,
    border: "none",
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: "zoom-in",
    overflow: "hidden",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  imageThumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transitionProperty: "transform",
    transitionDuration: tokens.durationNormal,
    ":hover": { transform: "scale(1.04)" },
  },
  imageMeta: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
  },
  imageMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalXS,
  },
  ellipsis: {
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  coverBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
  },
  fileIcon: {
    fontSize: "20px",
    color: tokens.colorCompoundBrandStroke,
    flexShrink: 0,
  },
  rowInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
  },
  linkUrl: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  composer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
  },
  composerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalXS,
  },
  lightboxSurface: {
    maxWidth: "90vw",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  lightboxImg: {
    maxWidth: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    borderRadius: tokens.borderRadiusMedium,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  micro: {
    fontSize: tokens.fontSizeBase100,
  },
});

function newAttachmentId(): string {
  return `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function displayUrl(url: string): string {
  try {
    const parsed = new URL(normalizeUrl(url));
    const path = parsed.pathname === "/" ? "" : safeDecode(parsed.pathname);
    return parsed.hostname.replace(/^www\./, "") + path + parsed.search;
  } catch {
    return url;
  }
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function attachmentDeleteTitle(attachment: AttachmentLink): string {
  if (isImageAttachment(attachment)) return "이미지 삭제";
  if (attachment.kind === "link") return "링크 삭제";
  return "파일 삭제";
}

function titleFromUrl(url: string): string {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./, "") || url;
  } catch {
    return url;
  }
}

export function isImageAttachment(a: AttachmentLink): boolean {
  return a.kind === "image" || !!a.mimeType?.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(a.url) || a.url.startsWith("data:image/");
}

function readFile(file: File, addedBy: string): Promise<AttachmentLink> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        id: newAttachmentId(),
        title: file.name,
        url: reader.result as string,
        kind: file.type.startsWith("image/") ? "image" : "file",
        size: file.size,
        mimeType: file.type,
        addedAt: new Date().toISOString(),
        ...(addedBy ? { addedBy } : {}),
      });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export interface AttachmentPanelProps {
  value: AttachmentLink[];
  onChange: (next: AttachmentLink[]) => void;
  /** Omit both to hide the "card cover" controls. */
  coverId?: string;
  onCoverChange?: (id: string | undefined) => void;
  /** 목록만 보여 주고 업로드·삭제·커버 변경은 숨긴다. */
  readOnly?: boolean;
}

/** Files, pasted images and web links for any detail drawer (Task, 이슈·결정). */
export function AttachmentPanel({ value, onChange, coverId, onCoverChange, readOnly = false }: AttachmentPanelProps) {
  const styles = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxImg, setLightboxImg] = useState<AttachmentLink | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AttachmentLink | null>(null);
  const [linkEditor, setLinkEditor] = useState<{ mode: "add" } | { mode: "edit"; id: string } | null>(null);
  const [kindFilter, setKindFilter] = useState<"files" | "links">("files");
  const canCover = !!onCoverChange;
  const me = useCatalogStore((state) => state.profile.displayName).trim();

  const images = useMemo(() => value.filter(isImageAttachment), [value]);
  const nonImages = useMemo(() => value.filter((a) => !isImageAttachment(a)), [value]);
  const files = useMemo(() => nonImages.filter((a) => a.kind === "file" || a.url.startsWith("data:") || a.size !== undefined), [nonImages]);
  const links = useMemo(() => nonImages.filter((a) => !files.includes(a)), [nonImages, files]);

  const handleFiles = async (fileList: FileList | File[] | null, imagesOnly = false) => {
    const picked = Array.from(fileList ?? []).filter((f) => !imagesOnly || f.type.startsWith("image/"));
    if (!picked.length) return;
    const added = await Promise.all(picked.map((file) => readFile(file, me)));
    onChange([...valueRef.current, ...added]);
  };

  const remove = (id: string) => {
    onChange(value.filter((x) => x.id !== id));
    if (coverId === id) onCoverChange?.(undefined);
  };

  const commitLink = (title: string, url: string, id?: string) => {
    const normalized = normalizeUrl(url);
    const resolvedTitle = title.trim() || titleFromUrl(normalized);
    if (id) {
      onChange(value.map((a) => (a.id === id ? { ...a, title: resolvedTitle, url: normalized } : a)));
      return;
    }
    onChange([...value, { id: newAttachmentId(), title: resolvedTitle, url: normalized, kind: "link", addedAt: new Date().toISOString(), ...(me ? { addedBy: me } : {}) }]);
  };

  const stop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (readOnly) {
    if (value.length === 0) return <Caption1 className={styles.muted}>첨부 없음</Caption1>;
    return (
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
        {value.map((item) => (
          <li key={item.id}>
            <a href={item.url} target="_blank" rel="noreferrer" title={item.title}>
              {item.title}
            </a>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      className={styles.root}
      onPaste={(e) => {
        if (kindFilter !== "files") return;
        const pasted = extractClipboardImageFiles(e.clipboardData);
        if (!pasted.length) return;
        e.preventDefault();
        void handleFiles(pasted, true);
      }}
    >
      <div className={styles.filterBar}>
        <div className={styles.filterGroup} role="tablist" aria-label="첨부 종류">
          {(["files", "links"] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kindFilter === k}
              className={mergeClasses(styles.filterChip, kindFilter === k && styles.filterChipActive)}
              onClick={() => setKindFilter(k)}
            >
              {k === "files" ? `파일${images.length + files.length ? ` ${images.length + files.length}` : ""}` : `링크${links.length ? ` ${links.length}` : ""}`}
            </button>
          ))}
        </div>
      </div>

      {kindFilter === "files" && (
        <>
          <div
            className={mergeClasses(styles.dropzone, isDragging && styles.dropzoneActive)}
            onDragOver={(e) => {
              stop(e);
              setIsDragging(true);
            }}
            onDragEnter={(e) => {
              stop(e);
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              stop(e);
              setIsDragging(false);
            }}
            onDrop={(e) => {
              stop(e);
              setIsDragging(false);
              void handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="파일 끌어다 놓기 또는 클릭하여 업로드"
          >
            <CloudArrowUp20Regular style={{ color: tokens.colorCompoundBrandStroke, fontSize: 24 }} />
            <Caption1 style={{ color: tokens.colorNeutralForeground2 }}>파일을 끌어다 놓거나 클릭하여 업로드</Caption1>
            <Caption1 className={mergeClasses(styles.muted, styles.micro)}>이미지는 Ctrl+V로 붙여넣기</Caption1>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                void handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {images.length > 0 && (
            <div className={styles.group}>
              <Caption1 className={styles.muted}>이미지 ({images.length})</Caption1>
              <div className={styles.imageGrid}>
                {images.map((img) => {
                  const isCover = coverId === img.id;
                  return (
                    <div key={img.id} className={styles.imageCard}>
                      <button type="button" className={styles.imageThumbWrap} onClick={() => setLightboxImg(img)} aria-label={`${img.title} 크게 보기`} title="클릭하여 크게 보기">
                        <img src={img.url} alt="" className={styles.imageThumb} />
                      </button>
                      <div className={styles.imageMeta}>
                        <div className={styles.imageMetaRow}>
                          <span className={styles.ellipsis} title={img.title}>
                            {img.title}
                          </span>
                          <Tooltip content="삭제" relationship="label">
                            <FuiButton appearance="subtle" density="dialog" icon={<Delete16Regular />} aria-label="이미지 삭제" onClick={() => setPendingDelete(img)} />
                          </Tooltip>
                        </div>
                        <div className={styles.imageMetaRow}>
                          <Caption1 className={mergeClasses(styles.muted, styles.micro)}>{formatBytes(img.size) || formatDate(img.addedAt, "dateTime")}</Caption1>
                          {canCover &&
                            (isCover ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <span className={styles.coverBadge}>
                                  <Star16Filled /> 커버
                                </span>
                                <FuiButton appearance="subtle" density="dialog" className={styles.micro} onClick={() => onCoverChange?.(undefined)}>
                                  해제
                                </FuiButton>
                              </div>
                            ) : (
                              <FuiButton appearance="subtle" density="dialog" icon={<Star16Regular />} className={styles.micro} onClick={() => onCoverChange?.(img.id)}>
                                커버 지정
                              </FuiButton>
                            ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className={styles.group}>
              <Caption1 className={styles.muted}>문서 및 파일 ({files.length})</Caption1>
              {files.map((f) => {
                const isPdf = f.title.toLowerCase().endsWith(".pdf") || f.mimeType === "application/pdf";
                const size = formatBytes(f.size);
                return (
                  <div key={f.id} className={styles.row}>
                    {isPdf ? <DocumentPdf16Regular className={styles.fileIcon} /> : <Document16Regular className={styles.fileIcon} />}
                    <div className={styles.rowInfo}>
                      <span className={styles.ellipsis} title={f.title}>
                        {f.title}
                      </span>
                      <Caption1 className={mergeClasses(styles.muted, styles.micro)}>
                        {size ? `${size} · ` : ""}
                        {formatDate(f.addedAt, "dateTime")}
                      </Caption1>
                    </div>
                    <div className={styles.rowActions}>
                      <Tooltip content="다운로드" relationship="label">
                        <FuiButton as="a" href={f.url} download={f.title} onClick={desktopDownloadClick(f.title, f.url)} appearance="subtle" density="dialog" icon={<ArrowDownload16Regular />} aria-label="다운로드" />
                      </Tooltip>
                      <Tooltip content="파일 삭제" relationship="label">
                        <FuiButton appearance="subtle" density="dialog" icon={<Delete16Regular />} aria-label="파일 삭제" onClick={() => setPendingDelete(f)} />
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {kindFilter === "links" && (
        <div className={styles.group}>
          <div className={styles.groupHead}>
            <Caption1 className={styles.muted}>웹 링크{links.length ? ` (${links.length})` : ""}</Caption1>
            {!linkEditor && (
              <FuiButton appearance="subtle" density="dialog" icon={<Add16Regular />} onClick={() => setLinkEditor({ mode: "add" })}>
                링크 추가
              </FuiButton>
            )}
          </div>
          {links.map((a) =>
            linkEditor?.mode === "edit" && linkEditor.id === a.id ? (
              <LinkComposer
                key={`edit-${a.id}`}
                initialTitle={a.title}
                initialUrl={a.url}
                submitLabel="저장"
                onSubmit={(title, url) => {
                  commitLink(title, url, a.id);
                  setLinkEditor(null);
                }}
                onCancel={() => setLinkEditor(null)}
              />
            ) : (
              <div key={a.id} className={styles.row}>
                <Link16Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
                <div className={styles.rowInfo}>
                  <span className={styles.ellipsis} title={a.title}>
                    {a.title}
                  </span>
                  <Tooltip content={a.url} relationship="description">
                    <Caption1 className={mergeClasses(styles.muted, styles.micro, styles.linkUrl)}>{displayUrl(a.url)}</Caption1>
                  </Tooltip>
                </div>
                <div className={styles.rowActions}>
                  <Tooltip content="새 창에서 열기" relationship="label">
                    <FuiButton as="a" href={a.url} target="_blank" rel="noreferrer" appearance="subtle" density="dialog" icon={<Open16Regular />} aria-label="새 창에서 열기" />
                  </Tooltip>
                  <Tooltip content="링크 수정" relationship="label">
                    <FuiButton appearance="subtle" density="dialog" icon={<Edit16Regular />} aria-label="링크 수정" onClick={() => setLinkEditor({ mode: "edit", id: a.id })} />
                  </Tooltip>
                  <Tooltip content="링크 삭제" relationship="label">
                    <FuiButton appearance="subtle" density="dialog" icon={<Delete16Regular />} aria-label="링크 삭제" onClick={() => setPendingDelete(a)} />
                  </Tooltip>
                </div>
              </div>
            ),
          )}
          {linkEditor?.mode === "add" && (
            <LinkComposer
              submitLabel="추가"
              onSubmit={(title, url) => {
                commitLink(title, url);
                setLinkEditor(null);
              }}
              onCancel={() => setLinkEditor(null)}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={pendingDelete ? attachmentDeleteTitle(pendingDelete) : "삭제"}
        message={
          pendingDelete ? (
            <>
              이 첨부를 삭제할까요?
              {coverId === pendingDelete.id ? " 카드 커버도 함께 해제됩니다." : ""}
            </>
          ) : (
            ""
          )
        }
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) remove(pendingDelete.id);
          setPendingDelete(null);
        }}
      />

      {lightboxImg && (
        <Dialog open onOpenChange={() => setLightboxImg(null)}>
          <DialogSurface className={styles.lightboxSurface}>
            <DialogTitle action={<FuiButton appearance="subtle" icon={<Dismiss24Regular />} onClick={() => setLightboxImg(null)} aria-label="닫기" />}>{lightboxImg.title}</DialogTitle>
            <DialogContent style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: tokens.spacingVerticalM }}>
              <img src={lightboxImg.url} alt={lightboxImg.title} className={styles.lightboxImg} />
            </DialogContent>
            <DialogActions>
              {canCover &&
                (coverId === lightboxImg.id ? (
                  <FuiButton appearance="subtle" onClick={() => onCoverChange?.(undefined)}>
                    커버 이미지 해제
                  </FuiButton>
                ) : (
                  <FuiButton appearance="primary" icon={<Star16Filled />} onClick={() => onCoverChange?.(lightboxImg.id)}>
                    카드 커버로 지정
                  </FuiButton>
                ))}
              <FuiButton as="a" href={lightboxImg.url} download={lightboxImg.title} onClick={desktopDownloadClick(lightboxImg.title, lightboxImg.url)} appearance="outline" icon={<ArrowDownload16Regular />}>
                다운로드
              </FuiButton>
            </DialogActions>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}

function LinkComposer({
  initialTitle = "",
  initialUrl = "",
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initialTitle?: string;
  initialUrl?: string;
  submitLabel: string;
  onSubmit: (title: string, url: string) => void;
  onCancel: () => void;
}) {
  const styles = useStyles();
  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState(initialTitle);
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    const t = setTimeout(() => titleRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  const submit = () => {
    if (url.trim()) onSubmit(title, url);
  };

  return (
    <div
      className={styles.composer}
      onKeyDownCapture={(e) => {
        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
          e.preventDefault();
          submit();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <Field label="제목" size="small">
        <FuiInput ref={titleRef} density="dialog" placeholder="표시할 이름" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="링크 제목" />
      </Field>
      <Field label="주소" size="small">
        <FuiInput density="dialog" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="URL" />
      </Field>
      <div className={styles.composerActions}>
        <FuiButton appearance="secondary" density="dialog" onClick={onCancel}>
          취소
        </FuiButton>
        <FuiButton appearance="primary" density="dialog" onClick={submit} disabled={!url.trim()}>
          {submitLabel}
        </FuiButton>
      </div>
    </div>
  );
}
